import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Radio,
  MicVocal,
  MapPin,
  ClipboardList,
  Users,
  UserSquare2,
  PhoneCall,
  LogOut,
  UserCheck,
  Settings as SettingsIcon,
  ShieldCheck,
  MessageCircle,
  DatabaseBackup,
  Package,
  Truck,
  ShoppingBag,
  Paintbrush,
  Globe,
  ArrowLeft,
  Stethoscope,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import TrialBanner from "@/components/TrialBanner";
import { useOrderNotifications } from "@/hooks/useOrderNotifications";
import { versionLabel, versionTitle } from "@/lib/version";
import { canSee } from "@/lib/access";

const BASE_NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true, id: "nav-overview" },
  { to: "/dashboard/ivr", label: "IVR Builder", icon: Radio, id: "nav-ivr" },
  { to: "/dashboard/recordings", label: "Recordings", icon: MicVocal, id: "nav-recordings" },
  { to: "/dashboard/map", label: "Live Map", icon: MapPin, id: "nav-map" },
  { to: "/dashboard/tasks", label: "Tasks", icon: ClipboardList, id: "nav-tasks" },
  { to: "/dashboard/healthcare", label: "Healthcare", icon: Stethoscope, id: "nav-healthcare", needs: "healthcare" },
  { to: "/dashboard/marketplace", label: "Marketplace", icon: Package, id: "nav-marketplace" },
  { to: "/dashboard/orders", label: "Orders", icon: ShoppingBag, id: "nav-orders" },
  { to: "/dashboard/deliveries", label: "Deliveries", icon: Truck, id: "nav-deliveries" },
  { to: "/dashboard/shipping", label: "Shipping", icon: Truck, id: "nav-shipping" },
  { to: "/dashboard/branding", label: "Branding", icon: Paintbrush, id: "nav-branding" },
  { to: "/dashboard/domain", label: "Custom Domain", icon: Globe, id: "nav-domain", needs: "superAdmin" },
  { to: "/dashboard/employees", label: "Employees", icon: Users, id: "nav-employees" },
  { to: "/dashboard/customers", label: "Customers", icon: UserSquare2, id: "nav-customers" },
  { to: "/dashboard/attendance", label: "Attendance", icon: UserCheck, id: "nav-attendance" },
  { to: "/dashboard/calls", label: "Call Logs", icon: PhoneCall, id: "nav-calls" },
  { to: "/dashboard/chat", label: "Team Chat", icon: MessageCircle, id: "nav-chat" },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon, id: "nav-settings" },
];

// Only the super admin sees the "Admins" nav item.
const SUPER_ADMIN_NAV = [
  {
    to: "/dashboard/admins",
    label: "Admins",
    icon: ShieldCheck,
    id: "nav-admins",
  },
  {
    to: "/dashboard/backup",
    label: "Backup",
    icon: DatabaseBackup,
    id: "nav-backup",
  },
];

// Employees with only `can_manage_marketplace` see a stripped-down sidebar
// so they can't see other tenant/admin surfaces. Kept in the same order as
// BASE_NAV for consistency with the owner console.
const MARKETPLACE_EMPLOYEE_TABS = new Set([
  "/dashboard/marketplace",
  "/dashboard/orders",
  "/dashboard/shipping",
]);

export default function OwnerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const isMarketplaceEmp =
    user?.role === "employee" && !!user?.can_manage_marketplace;

  const NAV = (isMarketplaceEmp
    ? BASE_NAV.filter((n) => MARKETPLACE_EMPLOYEE_TABS.has(n.to))
    : user?.is_super_admin
      ? [...BASE_NAV, ...SUPER_ADMIN_NAV]
      : BASE_NAV
  ).filter((n) => canSee(user, n.needs));

  // Live pending-order count for the sidebar Orders badge. The hook also
  // fires a toast + beep on genuinely-new orders while the tab is open.
  const { pendingCount } = useOrderNotifications({ enabled: !!user });

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[var(--app-bg)]">
      <aside className="hidden md:flex md:w-[260px] flex-col bg-[#0A0F1C] text-white">
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-2">
          <img
            src="/logo.png"
            alt="Computer Care"
            className="w-9 h-9 rounded-md bg-white p-0.5 object-contain"
          />
          <div>
            <div className="font-heading font-bold leading-none">Computer Care</div>
            <div className="mono text-[10px] text-white/50 mt-1 uppercase tracking-widest">
              {isMarketplaceEmp ? "Catalog console" : "Owner console"}
            </div>
            <div
              className="mono text-[10px] text-white/40 mt-1"
              title={versionTitle()}
              data-testid="app-version"
            >
              {versionLabel()}
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {NAV.map((item) => {
            const badge = item.to === "/dashboard/orders" && pendingCount > 0 ? pendingCount : 0;
            return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm ${
                  isActive
                    ? "bg-[#0055FF] text-white"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1">{item.label}</span>
              {badge > 0 && (
                <span
                  className="mono text-[10px] font-semibold bg-red-500 text-white rounded-full px-1.5 min-w-[18px] h-[18px] inline-flex items-center justify-center animate-pulse"
                  data-testid="nav-orders-badge"
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
            );
          })}
          {/* Marketplace-employees keep their /app tabs (Home, Customers,
              Tasks, Calls, Chat). Give them a one-tap ticket back. */}
          {isMarketplaceEmp && (
            <NavLink
              to="/app"
              className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/10 border border-white/10 mt-2"
              data-testid="catalog-back-to-app"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="flex-1">Back to my tasks</span>
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#FF5000] grid place-items-center font-heading font-semibold">
              {(user?.name || "?").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="mono text-[10px] text-white/50 truncate">{user?.email}</div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10"
              data-testid="owner-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="md:hidden bg-[#0A0F1C] text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Computer Care"
              className="w-7 h-7 rounded-md bg-white p-0.5 object-contain"
            />
            <span className="font-heading font-bold">Computer Care</span>
          </div>
          <Button size="sm" variant="ghost" onClick={handleLogout} className="text-white">
            Logout
          </Button>
        </div>
        <div className="md:hidden overflow-x-auto border-b bg-white">
          <div className="flex gap-1 px-2 py-2 whitespace-nowrap">
            {NAV.map((item) => {
              const badge = item.to === "/dashboard/orders" && pendingCount > 0 ? pendingCount : 0;
              return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 ${
                    isActive ? "bg-[#0055FF] text-white" : "bg-slate-100 text-slate-700"
                  }`
                }
              >
                {item.label}
                {badge > 0 && (
                  <span className="mono text-[9px] font-semibold bg-red-500 text-white rounded-full px-1 min-w-[16px] h-[16px] inline-flex items-center justify-center">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </NavLink>
              );
            })}
          </div>
        </div>
        <TrialBanner />
        {user && !user.is_super_admin && Number.isFinite(user.days_until_expiry) && user.days_until_expiry <= 7 && user.days_until_expiry >= 0 && (
          <div
            className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-center gap-2"
            data-testid="owner-expiry-banner"
          >
            <span className="text-amber-700">⏳</span>
            <div className="flex-1">
              Your admin access {user.days_until_expiry === 0
                ? <b>expires today.</b>
                : <>expires in <b>{user.days_until_expiry} day{user.days_until_expiry === 1 ? "" : "s"}</b>.</>
              } Contact the super admin to extend it before you lose access.
            </div>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  );
}
